import axios from "axios";
import { toast } from "sonner";

let limitExceededHandler = null;

// Guard so we only trigger ONE logout + redirect + toast per session,
// even if many requests fail simultaneously with 401.
let unauthorizedInFlight = false;

/**
 * Registrar un manejador global para errores de límite de plan
 * Este manejador se llamará cuando se detecte un error 403 con limit_exceeded
 */
export const registerLimitExceededHandler = (handler) => {
  limitExceededHandler = handler;
};

/**
 * Configurar interceptor de respuesta de Axios
 * Detecta errores 403 con {"detail": "limit_exceeded", "limit_type": "..."}
 * y dispara el modal de upgrade.
 *
 * También maneja 401 (sesión expirada) haciendo logout limpio + redirect a /login
 * con un toast — evita que la app se quede en un estado zombi tras la expiración
 * del JWT.
 */
export const setupAxiosInterceptors = () => {
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error.response?.status;

      // ── 401: sesión inválida/expirada ─────────────────────────────────
      // Ignoramos 401 procedente del propio endpoint de login (credenciales
      // incorrectas) — ese caso lo maneja la pantalla de login directamente.
      // También ignoramos el probe silencioso `/api/auth/me`: sin cookie de
      // sesión ese endpoint responde 401 de forma esperada al arrancar la app.
      const requestUrl = error.config?.url || "";
      const isLoginCall = /\/api\/auth\/login\b/.test(requestUrl);
      const isMeProbe = /\/api\/auth\/me\b/.test(requestUrl);

      if (status === 401 && !isLoginCall && !isMeProbe && !unauthorizedInFlight) {
        unauthorizedInFlight = true;
        try {
          // Cookie-only session: llamamos al backend para que borre las
          // cookies `access_token` y `sa_token`. Sin Bearer en localStorage
          // que limpiar. Legacy cleanup por si arrastramos claves antiguas.
          localStorage.removeItem("token");
          localStorage.removeItem("super_admin_token");

          // Best-effort: invalidar cookie httpOnly en backend. Con credentials
          // incluidas (axios.defaults.withCredentials=true) esta petición
          // envía la cookie y el backend la borra.
          try {
            const backend = process.env.REACT_APP_BACKEND_URL || "";
            fetch(`${backend}/api/auth/logout`, {
              method: "POST",
              credentials: "include",
              headers: { "Content-Type": "application/json" },
            }).catch(() => {});
          } catch (_) { /* ignore */ }

          toast.error("Tu sesión ha expirado. Vuelve a iniciar sesión.", {
            duration: 4000,
          });
          // Redirect duro para reiniciar el estado de la app (contextos, SW,
          // datos en memoria). Preservamos la ruta actual como redirect_to
          // por si en el futuro se quiere retomar tras el login.
          const redirectTo = encodeURIComponent(
            window.location.pathname + window.location.search
          );
          if (!window.location.pathname.startsWith("/login")) {
            window.location.replace(`/login?redirect=${redirectTo}`);
          }
        } catch (e) {
          // Never let the interceptor crash — worst case the caller sees
          // the raw 401 and handles it locally.
          console.error("[axiosInterceptor] 401 handler failed:", e);
        }
        return Promise.reject(error);
      }

      // ── 402 TRIAL_EXPIRED ────────────────────────────────────────────
      // Safety net: any operational call from a locked store redirects to
      // the full-screen "choose a plan" page.
      if (status === 402) {
        const code = error.response.data?.detail?.code;
        if (
          code === "TRIAL_EXPIRED" &&
          !window.location.pathname.startsWith("/prueba-caducada") &&
          !window.location.pathname.startsWith("/seleccionar-plan") &&
          !window.location.pathname.startsWith("/pago-exitoso")
        ) {
          window.location.replace("/prueba-caducada");
          return Promise.reject(error);
        }
      }

      // ── 403 PLAN_LIMIT_EXCEEDED ─────────────────────────────────────
      if (status === 403) {
        const errorData = error.response.data;
        if (errorData?.detail?.error === "PLAN_LIMIT_EXCEEDED" && errorData?.detail?.limit_type) {
          const limitData = errorData.detail;
          if (limitExceededHandler) {
            limitExceededHandler({
              limitType: limitData.limit_type,
              currentCount: limitData.current_count || 0,
              maxAllowed: limitData.max_allowed || 0,
              planName: limitData.plan_name || "Plan Actual"
            });
          }
          return Promise.reject(error);
        }
      }

      // ── 429 rate limited ─────────────────────────────────────────────
      // Show a lightweight toast so the user understands why the action
      // was blocked. Do NOT redirect — the operation may be retried later.
      if (status === 429) {
        toast.error("Has hecho demasiadas peticiones. Espera unos segundos e inténtalo de nuevo.", {
          duration: 4000,
        });
      }

      return Promise.reject(error);
    }
  );
};

export default setupAxiosInterceptors;
